import { Skeleton, Alert, VStack } from '@chakra-ui/react';
import React, { useEffect, useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';
import { DbGraphUtils } from '@finley-technologies/tape-builder';

import { useGetRecentMeshRunsQuery } from '../../requests/dataPipeline.gql.generated';
import { MeshCard } from 'packages/dataPipeline/ui/list/MeshCard';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';

const getEndOfDayEpoch = (date: Date): number => {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return commonDate.dateToEpoch(endOfDay);
};

const getStartOfDayEpoch = (date: Date): number => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  return commonDate.dateToEpoch(startOfDay);
};
export const MeshQueryTable = ({
  organizationId,
  startDate,
  endDate,
  shouldRefetch,
}: {
  organizationId: string;
  startDate: Date;
  endDate: Date;
  // parent component can trigger refetch of data by
  // updating this prop.
  shouldRefetch: boolean;
}) => {
  const flags = useFeatureFlags();
  const { data, loading, error, refetch } = useGetRecentMeshRunsQuery({
    variables: {
      organizationId,
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide,
      startDate: getStartOfDayEpoch(startDate),
      endDate: getEndOfDayEpoch(endDate),
    },
    fetchPolicy: 'no-cache',
  });

  // Trigger refetch when parent calls refetchData
  useEffect(() => {
    if (shouldRefetch) {
      refetch();
    }
  }, [shouldRefetch, refetch]);

  const meshTable = useMemo(() => {
    const serializedRecords = data?.meshRuns || [];
    const meshRecords: DataPipeline.MeshRunRecord[] = serializedRecords.map(
      DbGraphUtils.deserializeMeshRunRecord,
    );
    return meshRecords || [];
  }, [data?.meshRuns]);

  if (loading) {
    return <Skeleton height="600px" />;
  }

  if (error) {
    const errorMessage = `Error fetching Pipelines: ${error.message}`;
    return <Alert> {errorMessage} </Alert>;
  }

  if (meshTable.length === 0) {
    return (
      <Typography variant="base-muted">
        No pipelines to show for the selected date range.
      </Typography>
    );
  }

  return (
    <VStack spacing="16px" align="stretch">
      {meshTable.map((meshRow) => (
        <MeshCard key={meshRow.meshRunId} meshRow={meshRow} />
      ))}
    </VStack>
  );
};
